// hooks/useUserData.js - ULTRA FIXED - Eliminates ALL infinite loops

import { useState, useEffect, useRef } from 'react'

export const useUserData = () => {
  const [userData, setUserData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userCounts, setUserCounts] = useState({
    notifications: 0,
    chats: 0,
    cartItems: 0,
    favorites: 0
  })

  // CRITICAL: Use refs to prevent infinite loops - NO DEPENDENCIES
  const initializationStarted = useRef(false)
  const mounted = useRef(true)
  const initializationComplete = useRef(false)

  // ULTRA-FIXED: Pure function with NO useCallback to prevent dependency issues
  const validateTokenSync = async (token) => {
    if (!token || !mounted.current) return false

    try {
      // Check if user is in profile creation mode - skip token verification
      const currentPath = window.location.pathname
      const isProfileCreation = currentPath.includes('/auth/complete-profile/') ||
        currentPath.includes('/auth/create-password/') ||
        currentPath.includes('/auth/register/') ||
        currentPath.includes('/auth/google-register/') ||
        currentPath.includes('/auth/apple-register/') ||
        currentPath.includes('/auth/google-callback/')

      if (isProfileCreation) {
        // Return user data from localStorage without server verification
        const storedUserData = localStorage.getItem('user_data')
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData)
            if (userData && (userData.id || userData.user_id)) {
              return userData
            }
          } catch (error) {
            console.warn('⚠️ Failed to parse stored user data during profile creation')
          }
        }
        return null
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'


      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 8000) // 8 second timeout

      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)


      if (response.ok) {
        const data = await response.json()

        // Handle different response structures from Laravel
        let userData = null

        if (data.data) {
          userData = data.data
        } else if (data.user) {
          userData = data.user
        } else if (data.id || data.user_id) {
          userData = data
        }


        if (userData && (userData.id || userData.user_id)) {
          return userData
        } else {
          console.warn('🔍 Invalid user data structure:', userData)
          return false
        }
      } else if (response.status === 401) {
        return false
      } else {
        console.warn('🔍 Unexpected response status:', response.status)
        return false
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('🔍 Token validation timed out')
      } else {
        console.error('🔍 Token validation failed:', error)
      }
      return false
    }
  }

  // ULTRA-FIXED: Pure function with NO useCallback to prevent dependency issues
  const clearAuthDataSync = () => {

    const authKeys = [
      'access_token',
      'refresh_token',
      'user_data',
      'device_id',
      'verification_data',
      'verification_id',
      'intended_route'
    ]

    authKeys.forEach(key => {
      try {
        localStorage.removeItem(key)
      } catch (error) {
        console.warn(`Failed to remove ${key} from localStorage:`, error)
      }
    })

    if (mounted.current) {
      setUserData(null)
      setIsAuthenticated(false)
      setUserCounts({ notifications: 0, chats: 0, cartItems: 0, favorites: 0 })
    }
  }

  // ULTRA-FIXED: Pure initialization function with NO dependencies
  const initializeUserDataSync = async () => {
    // CRITICAL: Single initialization guard
    if (initializationStarted.current || !mounted.current || initializationComplete.current) {
      return
    }

    initializationStarted.current = true

    try {
      if (mounted.current) {
        setIsLoading(true)
      }


      const token = localStorage.getItem('access_token')
      const storedUserData = localStorage.getItem('user_data')


      if (!token) {
        clearAuthDataSync()
        initializationComplete.current = true
        return
      }

      // Always validate token with backend
      const validatedData = await validateTokenSync(token)

      if (validatedData && mounted.current) {
        setUserData(validatedData)
        setIsAuthenticated(true)

        // Update localStorage with fresh data from server
        localStorage.setItem('user_data', JSON.stringify(validatedData))

        // Set demo counts for test user
        if (validatedData.email === 'test@example.com') {
          setUserCounts({
            notifications: 3,
            chats: 2,
            cartItems: 5,
            favorites: 8
          })
        }
      } else {
        clearAuthDataSync()
      }

      initializationComplete.current = true

    } catch (error) {
      console.error('🚀 Error initializing user data:', error)
      if (mounted.current) {
        clearAuthDataSync()
      }
      initializationComplete.current = true
    } finally {
      if (mounted.current) {
        setIsLoading(false)
      }
    }
  }

  // ULTRA-CRITICAL: Single effect with NO dependencies - runs only once
  useEffect(() => {
    mounted.current = true

    // Only run if not already started
    if (!initializationStarted.current && !initializationComplete.current) {
      initializeUserDataSync()
    }

    // Cleanup function
    return () => {
      mounted.current = false
      // Don't reset refs here to prevent re-initialization
    }
  }, []) // EMPTY DEPENDENCY ARRAY - NEVER CHANGES

  // Manual refresh function - creates new instance each time to prevent stale closures
  const refreshUserData = async () => {
    // Don't refresh if already initializing
    if (initializationStarted.current && !initializationComplete.current) {
      return null
    }

    const token = localStorage.getItem('access_token')
    if (!token || !mounted.current) return null

    try {
      const validatedData = await validateTokenSync(token)

      if (validatedData && mounted.current) {
        setUserData(validatedData)
        localStorage.setItem('user_data', JSON.stringify(validatedData))
        return validatedData
      } else {
        clearAuthDataSync()
        return null
      }
    } catch (error) {
      console.error('🔄 Error refreshing user data:', error)
      return null
    }
  }

  // Update user data function - no useCallback to prevent deps
  const updateUserData = (newUserData) => {
    if (!newUserData || typeof newUserData !== 'object' || !mounted.current) return

    setUserData(currentUserData => {
      const updatedData = { ...currentUserData, ...newUserData }
      localStorage.setItem('user_data', JSON.stringify(updatedData))
      return updatedData
    })
  }

  // Logout function - no useCallback to prevent deps
  const logout = async () => {
    try {
      const token = localStorage.getItem('access_token')

      // Clear local data first
      clearAuthDataSync()

      // Reset initialization state
      initializationStarted.current = false
      initializationComplete.current = false

      // Then try to notify backend (non-blocking)
      if (token) {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

        try {
          await fetch(`${API_BASE_URL}/api/v1/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        } catch (error) {
          console.error('🚪 Backend logout failed:', error)
          // Continue anyway since local data is cleared
        }
      }

      return true
    } catch (error) {
      console.error('🚪 Logout error:', error)
      // Ensure cleanup even if error occurs
      clearAuthDataSync()

      return false
    }
  }

  // Manual reload function - resets initialization state
  const reloadUserData = async () => {

    // Reset state
    initializationStarted.current = false
    initializationComplete.current = false

    // Re-initialize
    await initializeUserDataSync()
  }

  // FIXED: Utility functions - no useCallback to prevent deps
  const getUserType = () => {
    if (!userData) return 'PET_GUARDIAN'

    const userType = userData.user_type || userData.userType || userData.type

    const typeMapping = {
      'PET_GUARDIAN': 'PET_GUARDIAN',
      'VETERINARIAN': 'VETERINARIAN',
      'SERVICE_PROVIDER': 'SERVICE_PROVIDER',
      'PET_SHOP_OWNER': 'SERVICE_PROVIDER', // Backend uses SERVICE_PROVIDER (legacy: PET_SHOP_OWNER maps to SERVICE_PROVIDER)
      'service_provider': 'SERVICE_PROVIDER', // Backend uses SERVICE_PROVIDER
      'pet_shop_owner': 'SERVICE_PROVIDER', // Backend uses SERVICE_PROVIDER (legacy: pet_shop_owner maps to SERVICE_PROVIDER)
      'CLINIC_MODERATOR': 'CLINIC_MODERATOR',
      'ADMIN': 'ADMIN'
    }

    return typeMapping[userType] || userType || 'PET_GUARDIAN'
  }

  const getDisplayName = () => {
    if (!userData) return 'User'

    if (userData.first_name && userData.last_name) {
      return `${userData.first_name} ${userData.last_name}`
    }

    if (userData.first_name) {
      return userData.first_name
    }

    return userData.display_name ||
      userData.username ||
      userData.name ||
      userData.email?.split('@')[0] ||
      'User'
  }

  const getProfileImageUrl = () => {
    if (!userData) return null

    // Special handling for demo user
    if (userData.email === 'test@example.com') {
      return "/icons/person.png"
    }

    const imageUrl = userData.profile_image_url ||
      userData.profileImageUrl ||
      userData.avatar ||
      userData.profile_image

    if (imageUrl) {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      if (imageUrl.startsWith('/storage/') ||
        imageUrl.startsWith('/uploads/')) {
        return `${API_BASE_URL}${imageUrl}`
      }

      if (imageUrl.startsWith('http')) {
        return imageUrl
      }

      return `${API_BASE_URL}/${imageUrl}`
    }

    return null
  }

  const getInitials = () => {
    const displayName = getDisplayName()
    return displayName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  const isDemoUser = () => {
    return userData?.email === 'test@example.com'
  }

  // Debug function to log current state
  const debugUserData = () => {
    // Debugging disabled
  }

  return {
    userData,
    isLoading,
    isAuthenticated,
    userCounts,

    // Functions - NO useCallback to prevent dependency loops
    refreshUserData,
    updateUserData,
    logout,
    clearAuthData: clearAuthDataSync,

    // Utility functions
    getUserType,
    getDisplayName,
    getProfileImageUrl,
    getInitials,
    isDemoUser,

    // Manual reload and debug
    reloadUserData,
    debugUserData
  }
}

export default useUserData